import { useEffect, useState } from 'react';

/** `setTimeout` cannot wait longer than a signed 32-bit number of milliseconds (~24.8 days). */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * "Now", re-read when the next of `expiries` passes (#95), so a row that expires while the page is open flips on
 * time instead of at the next unrelated re-render. One timer at a time, aimed at the soonest FUTURE expiry,
 * re-aimed after each firing; it is cleared on unmount and whenever the set of expiries changes (a tenant switch
 * replaces the list). Unparseable and already-past values are ignored.
 */
export function useExpiryClock(expiries: readonly string[]): number {
  // `tick` only exists to trigger a re-render; the value handed back is ALWAYS the current time. A cached "now" can be
  // hours old (no timer fires while nothing is due), and a list that arrives then would render an already-expired
  // token as Active, with no timer scheduled to fix it.
  const [tick, setNow] = useState(() => Date.now());
  const key = [...expiries].sort().join('|');
  // The time this render judged tokens by. The effect must filter against THIS, not a fresh `Date.now()`: a token
  // that expires between render and effect was Active in what the user sees, and would otherwise be classed "already
  // past" by the effect and never get a timer.
  const renderNow = Date.now();
  useEffect(() => {
    const upcoming = key
      .split('|')
      .filter((s) => s.length > 0)
      .map((s) => Date.parse(s))
      .filter((ms) => !Number.isNaN(ms) && ms > renderNow);
    if (upcoming.length === 0) return undefined;
    // A hair past the expiry, so the comparison `expiresAt <= now` is true when it fires; never negative (the expiry
    // may already have passed since the render, in which case it fires at once).
    const delay = Math.min(Math.max(0, Math.min(...upcoming) - Date.now() + 25), MAX_TIMER_MS);
    const id = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(id);
    // `tick` re-runs this after each firing with a fresh render time; `renderNow` is deliberately read from that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);
  return renderNow;
}
